import {
  DropdownMenuCheckboxItemProps,
  DropdownMenuContentProps,
  DropdownMenuItemProps,
} from '@radix-ui/react-dropdown-menu';
import {TooltipContentProps} from '@radix-ui/react-tooltip';
import {IconButton} from '@wandb/components';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {Icon, IconName} from '@wandb/weave/components/Icon';
import * as Tooltip from '@wandb/weave/components/RadixTooltip';
import React, {useState} from 'react';
import {twMerge} from 'tailwind-merge';

type OverflowMenuProps = {
  'aria-label': string;
  children: React.ReactNode;
  contentProps?: DropdownMenuContentProps;
  'data-test'?: string;
  isDisabled?: boolean;
  isRootModal?: boolean;
  tooltip?: string;
  variant?: 'ghost' | 'secondary';
};

export const OverflowMenu = ({
  'aria-label': ariaLabel,
  children,
  contentProps = {},
  'data-test': dataTest,
  isDisabled,
  isRootModal,
  tooltip,
  variant,
}: OverflowMenuProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  return (
    <Tooltip.Provider>
      <DropdownMenu.Root
        open={isMenuOpen}
        onOpenChange={setIsMenuOpen}
        modal={isRootModal}>
        <DropdownMenu.Trigger data-test={dataTest} asChild>
          <IconButton
            icon="overflow-horizontal"
            variant={variant || 'ghost'}
            onPress={e => e.continuePropagation()}
            isActive={isMenuOpen}
            isDisabled={isDisabled}
            aria-label={ariaLabel}
            tooltip={{children: tooltip}}
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            data-test="nw-overflow-menu-content"
            // z-index of 6 needed here to exceed resizeable workspace panels,
            // which go to z-5 when they are hovered.
            className="z-[6] shadow-md shadow-oblivion/[0.16] dark:shadow-oblivion/[0.48]"
            align="end"
            sideOffset={12}
            onCloseAutoFocus={e => e.preventDefault()}
            {...contentProps}>
            {children}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </Tooltip.Provider>
  );
};

type OverflowMenuItemProps = {
  displayName: string;
  icon: IconName;
  tooltipContent?: React.ReactElement | string;
  tooltipProps?: TooltipContentProps;
} & DropdownMenuItemProps;

export const OverflowMenuItem = ({
  className,
  displayName,
  disabled,
  icon,
  tooltipContent,
  tooltipProps = {},
  ...dropdownItemProps
}: OverflowMenuItemProps) => {
  return (
    <DropdownMenu.Item
      className={twMerge(
        'radix-disabled:pointer-events-auto radix-disabled:cursor-not-allowed',
        className
      )}
      disabled={disabled}
      {...dropdownItemProps}>
      <div className="flex w-full items-center justify-between gap-16">
        <div className="flex items-center gap-8">
          <Icon className="shrink-0" name={icon} role="presentation" />
          <span>{displayName}</span>
        </div>
        {tooltipContent && (
          <div className="night-aware flex">
            <IconButton
              variant="ghost"
              icon="info"
              size="sm"
              aria-label="Additional options"
              tooltip={{
                children: tooltipContent,
                placement: 'right',
              }}
            />
          </div>
        )}
      </div>
    </DropdownMenu.Item>
  );
};

export const OverflowMenuCheckboxItem = ({
  displayName,
  icon,
  ...dropdownCheckboxItemProps
}: {
  displayName: string;
  icon: IconName;
} & DropdownMenuCheckboxItemProps) => {
  return (
    <DropdownMenu.CheckboxItem
      className="flex justify-between text-moon-800 dark:text-moon-300"
      {...dropdownCheckboxItemProps}>
      <div className="flex items-center gap-8">
        <Icon className="shrink-0" name={icon} />
        {displayName}
      </div>
      <DropdownMenu.ItemIndicator>
        <Icon name="checkmark" width={18} height={18} />
      </DropdownMenu.ItemIndicator>
    </DropdownMenu.CheckboxItem>
  );
};

/**
 * Create a wrapped OverflowMenuItem component with preconfigured props.
 * Use this to configure consistent props across all items within a menu.
 */
export const makeOverflowMenuItem =
  ({
    tooltipProps: outerTooltipProps,
    ...outerProps
  }: Partial<OverflowMenuItemProps> = {}) =>
  ({
    tooltipProps: innerTooltipProps = {},
    ...innerProps
  }: OverflowMenuItemProps) => {
    return (
      <OverflowMenuItem
        tooltipProps={{
          ...outerTooltipProps,
          ...innerTooltipProps,
        }}
        {...outerProps}
        {...innerProps}
      />
    );
  };
